import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  memo
} from 'react'
import LockedFileAccess from 'features/documentAccess/LockedFileAccess'
import { LOCKED_EXPLAINER, SUCCESSION_EXPLAINER } from 'features/documentAccess/accessCopy'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { groupBy, isNil, pathOr, propOr, last, uniq } from 'ramda'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { canEditBp } from 'utils/user'
import { fetchBp } from 'services/BpService'
import { SkeletonRows } from 'components/Skeleton'
import moment from 'moment'
import { removeUnitFileRoutine } from 'ducks/units/actions'
import { removeFileRoutine, requestFileThumbnailsRoutine } from 'ducks/files/actions'
import { ReactComponent as FileIcon } from 'assets/images/file-icon.svg'
import ArticleIcon from '@mui/icons-material/Article'
import LockIcon from '@mui/icons-material/Lock'
import { formatDate } from 'utils/date'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import UnshareFileModal from 'features/bpDetails/Components/UnshareFileModal'
import Modal from 'components/atoms/Modal'
import EditPrivateFileModal from 'features/bpDetails/Components/EditPrivateFileModal'
import EditFileModal from 'features/bpDetails/Components/EditFileModal'
import { getAssignedUsers, getFloorOptions } from 'utils/bpData'
import ShareFileModal from 'features/bpDetails/Components/ShareFileModal'
import PdfFullScreenViewer from 'components/PdfFullScreenViewer'
import styled, { css } from 'styled-components'
import ShareIcon from '@mui/icons-material/Share'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined'
import AddNewFileVersionModal from 'features/bpDetails/Components/AddNewFileVersionModal'
import { isNotNilOrEmpty } from 'utils/ramda'
import AddUsersToFile from 'features/bpDetails/Components/files/AddUsersToFile'
import AttachTagToFile from 'features/bpDetails/Components/files/AttachTagToFile'
import FileItemTag from 'features/bpDetails/Components/files/FileItemTag'
import ImageViewer from 'components/ImageViewer'
import { markFileAsViewed, getFileUrls, markFilesForDeletion, toggleUserPermissionsToFiles } from 'services/fileService'
import { getEstateFileUrls } from 'services/estateDocumentsService'
import { toast } from 'react-toastify'
import ViewedStatusModal from 'features/bpDetails/Components/files/ViewedStatusModal'
import { markFileAsSearchedRoutine } from 'features/bpDetails/ducks/actions'
import Checkbox from 'components/atoms/Checkbox'
import { hideEmail } from 'utils/user'
import { USER_ROLES } from 'utils/userRoles'
import {
  logExternalDownloadRoutine,
  logExternalPreviewRoutine
} from 'features/bpDetails/ducks/actions'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import ReclassifyFileModal from 'features/bpDetails/Components/ReclassifyFileModal'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import * as users from 'ramda'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import ArchitectureIcon from '@mui/icons-material/Architecture'
import CircleIcon from '@mui/icons-material/Circle'
import useBodyScrollLock from 'hooks/useBodyScrollLock'
import { redirect } from 'utils/paths'
import IdentityRestrictedNote, { identityRestrictionText } from 'features/bpDetails/Components/files/IdentityRestrictedNote'
import IdentityDocumentExplainer from 'features/bpDetails/Components/files/IdentityDocumentExplainer'
import { RAG } from 'features/dashboard/charts/scoreColour'
import saveFilePermissions, { removedUserIds } from 'features/bpDetails/utils/saveFilePermissions'
import RemoveAccessConfirm from 'features/bpDetails/Components/files/RemoveAccessConfirm'

const permissionsBpPoolCache = new Map()

// Pass `file` for a single file, or `files` to manage access to several files at once.
// In bulk mode a user who can see only some of the files shows as partial; only users
// whose state was changed are written back (granted or revoked on every selected file).
export const FilePermissionsModal = ({ open, onClose, file, files = null, bp: bpProp = null, onSaved }) => {
  const dispatch = useDispatch()
  const isBulk = Array.isArray(files)
  const targetFiles = useMemo(() => (isBulk ? files : file ? [file] : []), [isBulk, files, file])
  const [partialUsers, setPartialUsers] = useState([])
  const [initialUsers, setInitialUsers] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [confirmingRemoval, setConfirmingRemoval] = useState(false)
  const selectedBp = useSelector(getSelectedBp)
  const currentUser = useSelector(getCurrentUser)
  const [selectedUsers, setSelectedUsers] = useState([])
  const [fetchedPoolBp, setFetchedPoolBp] = useState(null)
  const [isLoadingPool, setIsLoadingPool] = useState(false)
  const needsPoolFetch = Boolean(bpProp?.id) && bpProp.id !== selectedBp?.id

  const bp = needsPoolFetch
    ? (fetchedPoolBp?.id === bpProp.id ? fetchedPoolBp : bpProp)
    : selectedBp

  useEffect(() => {
    if (!open || !needsPoolFetch) return

    const cached = permissionsBpPoolCache.get(bpProp.id)
    if (cached) {
      setFetchedPoolBp(cached)
      return
    }

    let cancelled = false
    setIsLoadingPool(true)
    fetchBp(bpProp.id)
      .then(response => {
        const fullBp = response?.data?.data
        if (fullBp) {
          permissionsBpPoolCache.set(bpProp.id, fullBp)
          if (!cancelled) setFetchedPoolBp(fullBp)
        }
      })
      .catch(error => {
        console.error('Failed to fetch building user pool:', error)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPool(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, needsPoolFetch, bpProp?.id])

  const assignedUsers = useMemo(() => {
    const assigned =
      getAssignedUsers(bp)?.filter(user => user.id !== currentUser?.id) || []
    const grouped = groupBy(user => user.id, assigned)

    return Object.values(grouped).map(userAssignments => ({
      ...userAssignments[0],
      roles: uniq(userAssignments.map(user => user.relationType))
    }))
  }, [bp, currentUser?.id])

  useEffect(() => {
    if (!open || targetFiles.length === 0) return
    const counts = {}
    targetFiles.forEach(f => uniq(f.users || []).forEach(id => { counts[id] = (counts[id] || 0) + 1 }))
    const withAll = Object.keys(counts).filter(id => counts[id] === targetFiles.length)
    setSelectedUsers(withAll)
    setInitialUsers(withAll)
    setPartialUsers(Object.keys(counts).filter(id => counts[id] < targetFiles.length))
  }, [targetFiles, open])

  // Building owners can always see every file and the API ignores access changes for them,
  // so they are shown as granted and can't be toggled.
  const isOwnerUser = user => user.roles.includes('owner')
  const manageableUsers = assignedUsers.filter(user => !isOwnerUser(user))

  // file.users can hold ids that are not listed here (e.g. hidden staff), so compare per listed user
  const allUsersSelected =
    manageableUsers.length > 0 &&
    manageableUsers.every(user => selectedUsers.includes(user.id) && !partialUsers.includes(user.id))

  const toggleUser = userId => {
    setPartialUsers(current => current.filter(id => id !== userId))
    setSelectedUsers(current =>
      current.includes(userId)
        ? current.filter(id => id !== userId)
        : [...current, userId]
    )
  }

  const toggleAllUsers = () => {
    const listedIds = manageableUsers.map(user => user.id)
    setPartialUsers([])
    setSelectedUsers(current => {
      const unlisted = current.filter(id => !listedIds.includes(id))
      return allUsersSelected ? unlisted : uniq([...unlisted, ...listedIds])
    })
  }

  const handleBulkSave = async () => {
    const changedUsers = manageableUsers
      .map(user => user.id)
      .filter(id => !partialUsers.includes(id))
      .filter(id => selectedUsers.includes(id) !== initialUsers.includes(id))

    if (changedUsers.length === 0) {
      onClose()
      return
    }

    setIsSaving(true)
    const results = await Promise.allSettled(
      changedUsers.map(userId => {
        const permission = selectedUsers.includes(userId)
        return toggleUserPermissionsToFiles(
          bp.id,
          userId,
          targetFiles.map(f => ({ id: f.id, permission }))
        )
      })
    )
    setIsSaving(false)

    const failed = results.filter(r => r.status === 'rejected').length
    if (failed > 0) {
      toast.error(`Could not update access for ${failed} user${failed === 1 ? '' : 's'}. Please try again.`)
    } else {
      toast.success(`Access updated for ${targetFiles.length} file${targetFiles.length === 1 ? '' : 's'}.`)
    }
    onSaved?.()
    onClose()
  }

  const handleSave = () => {
    if (!bp?.id) return
    if (isBulk) {
      handleBulkSave()
      return
    }
    if (!file) return

    if (removedUserIds(file, selectedUsers).length > 0) {
      setConfirmingRemoval(true)
      return
    }
    saveFilePermissions({ bpId: bp.id, file, selectedUsers, dispatch })
    onClose()
  }

  const confirmRemoval = async () => {
    setIsSaving(true)
    try {
      await saveFilePermissions({ bpId: bp.id, file, selectedUsers, dispatch })
    } finally {
      setIsSaving(false)
      setConfirmingRemoval(false)
    }
    onClose()
  }

  return (
    <Modal open={open} title='Manage permissions' onClose={isSaving ? () => {} : onClose}>
      <PermissionsModalContent>
        {isLoadingPool ? (
          <SkeletonRows rows={4} gap={16} widths={['70%', '40%']} />
        ) : assignedUsers.length > 0 ? (
          <>
            <PermissionsDescription>
              {isBulk
                ? `Which users should have access to these ${targetFiles.length} files?`
                : 'Which users should have access to this file?'}
            </PermissionsDescription>
            {manageableUsers.length > 0 && (
              <PermissionsSelectAllButton onClick={toggleAllUsers}>
                {allUsersSelected ? 'DESELECT ALL USERS' : 'SELECT ALL USERS'}
              </PermissionsSelectAllButton>
            )}
            <PermissionsUserList>
              {assignedUsers.map(user => {
                const isOwner = isOwnerUser(user)
                const isSelected = isOwner || selectedUsers.includes(user.id)
                const isPartial = !isOwner && partialUsers.includes(user.id)
                return (
                  <PermissionsUserItem
                    key={user.id}
                    type='button'
                    onClick={() => !isOwner && toggleUser(user.id)}
                    disabled={isOwner}
                    $isSelected={isSelected}
                    $isLocked={isOwner}
                    title={isOwner ? 'Admins always have access to all files' : undefined}
                  >
                    <div>
                      <PermissionsUserEmail>{hideEmail(user.email)}</PermissionsUserEmail>
                      <PermissionsUserName>
                        {user.firstName}&nbsp;{user.lastName}
                      </PermissionsUserName>
                      <PermissionsUserRoles>
                        User role:{' '}
                        {user.roles
                          .map(role => USER_ROLES[role] || role)
                          .join(', ')}
                      </PermissionsUserRoles>
                      {isOwner && (
                        <PermissionsUserNote>Admins always have access to all files</PermissionsUserNote>
                      )}
                    </div>
                    <PermissionsCheck title={isPartial ? 'Has access to some of the selected files' : undefined}>
                      {isPartial ? '–' : isSelected ? '✓' : ''}
                    </PermissionsCheck>
                  </PermissionsUserItem>
                )
              })}
            </PermissionsUserList>
          </>
        ) : (
          <PermissionsEmptyState>No users to manage</PermissionsEmptyState>
        )}
        <PermissionsActions>
          <PermissionsCancelButton type='button' onClick={onClose}>
            Cancel
          </PermissionsCancelButton>
          <PermissionsSaveButton
            type='button'
            onClick={handleSave}
            disabled={assignedUsers.length === 0 || isSaving}
          >
            {isSaving ? 'Saving…' : 'Accept'}
          </PermissionsSaveButton>
        </PermissionsActions>
      </PermissionsModalContent>
      <RemoveAccessConfirm
        open={confirmingRemoval}
        count={removedUserIds(file, selectedUsers).length}
        everyone={selectedUsers.length === 0}
        busy={isSaving}
        onConfirm={confirmRemoval}
        onCancel={() => setConfirmingRemoval(false)}
      />
    </Modal>
  )
}

const File = ({
  file,
  selectedFile,
  handleFilePreview,
  type,
  isUnit,
  isShared,
  unit,
  isSharedWithUnits,
  isInSearchBar,
  viewMode = 'list',
  showThumbnail,
  categoryColor,
  rowIndex,
  isSelected = false,
  onSelect = (fileId, isChecked) => {},
  hideBulkSelect = false,
  customPath = null,
  onOpenReclassifyModal = null,
  onOpenEditModal = null,
  onOpenPermissionsModal = null,
  onOpenViewedStatusModal = null,
  externalModalControl = false,
  forceMobileMenu = false,
  disableSelectionHighlight = false,
  searchQuery = '',
  searchMode = 'name',
  isMobileSelectionMode = false,
  onActivateMobileSelectionMode = null,
  readOnlyAggregate = false,
  sourceBpName = null,
  estateId = null,
  bp: bpProp = null
}) => {
  const { uploader, expirationDate } = file
  const displayName = file.displayName || file.display_name || file.name

  // Function to highlight search query in text
  const highlightText = useCallback((text, query) => {
    if (!query || query.length < 3 || !text) return text

    const parts = []
    const lowerText = text.toLowerCase()
    const lowerQuery = query.toLowerCase()
    let lastIndex = 0
    let index = lowerText.indexOf(lowerQuery)

    while (index !== -1) {
      // Add text before match
      if (index > lastIndex) {
        parts.push(text.substring(lastIndex, index))
      }
      // Add highlighted match
      parts.push(
        <span
          key={`${index}-highlight`}
          style={{
            backgroundColor: '#ffeb3b',
            color: '#000',
            fontWeight: 700,
            padding: '1px 2px',
            borderRadius: '2px'
          }}
        >
          {text.substring(index, index + query.length)}
        </span>
      )
      lastIndex = index + query.length
      index = lowerText.indexOf(lowerQuery, lastIndex)
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex))
    }

    return parts.length > 0 ? parts : text
  }, [])

  const [anchorEl, setAnchorEl] = useState(null)
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false)
  const longPressTimerRef = useRef(null)
  const touchStartTimeRef = useRef(null)
  const touchStartPositionRef = useRef({ x: 0, y: 0 })
  const hasMovedRef = useRef(false)
  const fileElementRef = useRef(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [newVersionModalOpen, setNewVersionModalOpen] = useState(false)
  const [guruInsightModalOpen, setGuruInsightModalOpen] = useState(false)
  const [reclassifyModalOpen, setReclassifyModalOpen] = useState(false)
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false)
  const [viewedStatusModalOpen, setViewedStatusModalOpen] = useState(false)
  const [unshareModalOpen, setUnshareModalOpen] = useState(false)
  const bottomSheetOpenedAtRef = useRef(0)
  const open = Boolean(anchorEl)

  const dispatch = useDispatch()
  const fileDescription = useMemo(() => {
    return pathOr('', ['description'], file) || ''
  }, [file])

  const hasExpirationDate = useMemo(
    () => !isNil(expirationDate),
    [expirationDate]
  )
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [imageViewerOpen, setImageViewerOpen] = useState(false)
  const [fileUrls, setFileUrls] = useState(null)
  const [isLoadingUrls, setIsLoadingUrls] = useState(false)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const bp = useSelector(getSelectedBp)
  const isAggregate = readOnlyAggregate
  const rowCanEdit = Boolean(file?.buildingPassport?.canEdit ?? bpProp?.canEdit)
  const effectiveBp = bpProp ?? (isAggregate ? file?.buildingPassport : null) ?? bp
  const currentUser = useSelector(getCurrentUser)
  // Get thumbnail from separate store to avoid re-renders when other thumbnails load
  const thumbnailFromStore = useSelector(state => state.files.thumbnailsById[file.id])
  const thumbnailUrl = thumbnailFromStore !== undefined ? thumbnailFromStore : file.thumbnailUrl

  useEffect(() => {
    if (thumbnailFromStore !== undefined) return
    const thumbnailBpId = effectiveBp?.id
    if (!thumbnailBpId || !file?.id) return
    dispatch(requestFileThumbnailsRoutine.trigger({ bpId: thumbnailBpId, fileId: file.id }))
  }, [file?.id, effectiveBp?.id, thumbnailFromStore, dispatch])
  const isFloorPlan = pathOr('', ['fileType', 'name'], file) === 'Building plan'
  const isPhoto = pathOr('', ['fileType', 'name'], file) === 'Photo'
  const isDrawing = pathOr('', ['fileType', 'name'], file) === 'Drawings'

  const titleCase = s => s ? s.replace(/\b\w/g, c => c.toUpperCase()) : ''

  const drawingTags = useMemo(() => {
    if (!isDrawing || !file.taggings) return []
    return file.taggings
      .filter(t => t.tag?.layer)
      .map(t => ({
        name: t.tag?.name || t.name,
        layer: t.tag?.layer,
        isSystem: !!t.isSystem
      }))
  }, [isDrawing, file.taggings])

  const drawingStatus = useMemo(() => {
    const tag = drawingTags.find(t => t.layer === 'status')
    return tag?.name || null
  }, [drawingTags])

  const drawingDiscipline = useMemo(() => {
    const tag = drawingTags.find(t => t.layer === 'discipline')
    return tag?.name || null
  }, [drawingTags])

  const productTags = useMemo(() => {
    if (!file.taggings) return []
    return file.taggings
      .filter(t => (t.tag?.layer || t.layer) === 'product_guarantee')
      .map(t => t.tag?.name || t.name)
      .filter(Boolean)
  }, [file.taggings])

  const isAnyOverlayOpen =
    bottomSheetOpen ||
    modalOpen ||
    editModalOpen ||
    shareModalOpen ||
    newVersionModalOpen ||
    guruInsightModalOpen ||
    reclassifyModalOpen ||
    permissionsModalOpen ||
    viewedStatusModalOpen ||
    unshareModalOpen ||
    pdfViewerOpen ||
    imageViewerOpen

  useBodyScrollLock(isAnyOverlayOpen)

  const isExcelFile = useMemo(() => {
    const fileName = displayName || file.name || ''
    return /\.(xlsx|xls|xlsm|xlsb)$/i.test(fileName)
  }, [displayName, file.name])
  const canBeAccessed = file?.canBeAccessed !== false
  const lockedExplainer = `${LOCKED_EXPLAINER}${file?.accessLockedBySuccession ? ` ${SUCCESSION_EXPLAINER}` : ''}`
  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleMarkForSharePointCleanse = async () => {
    setAnchorEl(null)
    const bpId = effectiveBp?.id || file.buildingPassport?.id
    if (!bpId) return
    try {
      await markFilesForDeletion(bpId, [file.id])
      toast.success('Marked for deletion — see the SharePoint cleanse tab')
    } catch (e) {
      toast.error('Could not mark this file. Please try again.')
    }
  }

  const latestVersion = useMemo(() => {
    return propOr([], 'versions', file).length
  }, [file])

  const latestVersionCreationDate = useMemo(() => {
    const versions = propOr([], 'versions', file).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt)
    )

    return last(versions)?.createdAt || null
  }, [file])

  const [collapsed, setCollapsed] = useState(false)
  const toggleCollapse = () => setCollapsed(prev => !prev)

  const handlePdfViewerClose = () => setPdfViewerOpen(false)
  const handleImageViewerClose = () => setImageViewerOpen(false)
  const isUploader = file.uploader?.id === currentUser?.id
  const canEdit = !readOnlyAggregate && (isUploader || (canEditBp(currentUser, bp) && !file.isExternal))
  const sourceBpId = file.buildingPassport?.id
  const sourceBpDisplayName = file.buildingPassport?.name || 'Building Passport'
  const handleOpenInBuilding = () => {
    if (sourceBpId) redirect(`bp-list/${sourceBpId}/files`)
  }

  const handlePermissionsModalOpen = () => {
    setAnchorEl(null)
    setBottomSheetOpen(false)
    if (externalModalControl && onOpenPermissionsModal) {
      onOpenPermissionsModal(file)
      return
    }
    setPermissionsModalOpen(true)
  }

  const handleViewedStatusModalOpen = () => {
    setAnchorEl(null)
    setBottomSheetOpen(false)
    if (externalModalControl && onOpenViewedStatusModal) {
      onOpenViewedStatusModal(file)
      return
    }
    setViewedStatusModalOpen(true)
  }

  const isExpired = useMemo(() => {
    return hasExpirationDate
      ? moment(expirationDate).isSameOrBefore(moment())
      : false
  }, [hasExpirationDate])

  const openMenu = e => {
    e.stopPropagation()
    e.preventDefault()

    const isMobile =
      forceMobileMenu ||
      window.innerWidth <= 768 ||
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ||
      (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) ||
      (typeof window !== 'undefined' && 'ontouchstart' in window)
    if (isMobile) {
      bottomSheetOpenedAtRef.current = Date.now()
      setTimeout(() => {
        setBottomSheetOpen(true)
      }, 0)
    } else {
      const target = e.currentTarget
      setAnchorEl(target)
    }
  }
  const handleClose = e => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    setAnchorEl(null)
  }

  const handleBottomSheetClose = () => {
    const now = Date.now()
    const sinceOpen = now - bottomSheetOpenedAtRef.current
    if (sinceOpen < 200) {
      return
    }
    setBottomSheetOpen(false)
  }

  const lastBottomSheetTouchRef = useRef(0)
  const runBottomSheetAction = useCallback(
    (action, handler) => e => {
      if (e) {
        e.stopPropagation()
        if (e.type === 'touchstart') {
          lastBottomSheetTouchRef.current = Date.now()
          e.preventDefault()
        } else {
          const sinceTouch = Date.now() - lastBottomSheetTouchRef.current
          if (sinceTouch < 400) {
            return
          }
        }
      }

      handleBottomSheetClose()
      setTimeout(handler, 50)
    },
    [handleBottomSheetClose]
  )

  useEffect(() => {}, [])

  const handleModalOpen = e => {
    e.stopPropagation()
    e.preventDefault()
    setAnchorEl(null)
    setModalOpen(true)
  }
  const handleModalClose = () => setModalOpen(false)

  const shouldBlockForDragSelection = e => {
    const isDragSelecting = window.__isDragSelecting
    if (!isDragSelecting) return false

    const mouseMovement = window.__dragMovement || 0
    if (mouseMovement <= 10) return false

    if (forceMobileMenu) return false

    const pointerType = e?.nativeEvent?.pointerType
    if (pointerType && pointerType !== 'mouse') return false

    if (e?.nativeEvent?.sourceCapabilities?.firesTouchEvents) return false

    return true
  }

  const handleEditModalOpen = e => {
    // Only prevent modal if we're actually drag selecting (not just mouse down)
    if (shouldBlockForDragSelection(e)) {
      return
    }

    e.stopPropagation()
    e.preventDefault()
    setAnchorEl(null)

    if (externalModalControl && typeof onOpenEditModal === 'function') {
      onOpenEditModal(file)
      return
    }

    setEditModalOpen(true)
  }
  const handleEditModalClose = () => setEditModalOpen(false)

  const handleShareModalOpen = e => {
    // Only prevent modal if we're actually drag selecting (not just mouse down)
    if (shouldBlockForDragSelection(e)) {
      return
    }

    e.stopPropagation()
    e.preventDefault()
    setAnchorEl(null)
    setShareModalOpen(true)
  }
  const handleShareModalClose = () => setShareModalOpen(false)

  const handleGuruInsightModalOpen = e => {
    // Only prevent modal if we're actually drag selecting (not just mouse down)
    if (shouldBlockForDragSelection(e)) {
      return
    }

    e.stopPropagation()
    e.preventDefault()
    setAnchorEl(null)
    setGuruInsightModalOpen(true)
  }
  const handleGuruInsightModalClose = () => setGuruInsightModalOpen(false)

  const handleReclassifyModalOpen = useCallback(
    e => {
      // Only prevent modal if we're actually drag selecting (not just mouse down)
      if (shouldBlockForDragSelection(e)) {
        return
      }

      e.stopPropagation()
      e.preventDefault()
      setAnchorEl(null)

      if (!file?.canBeAccessed) {
        import('react-toastify').then(({ toast }) => {
          toast.error(
            'You cannot re-classify this file because you do not have permission to access it.'
          )
        })
        return
      }

      if (externalModalControl && onOpenReclassifyModal) {
        // Use external handler (for custom views to prevent unmount issues)
        onOpenReclassifyModal()
      } else {
        // Use local state (for automatic views)
        setReclassifyModalOpen(true)
      }
    },
    [externalModalControl, onOpenReclassifyModal, file]
  )

  const handleDirectReclassifyModalOpen = useCallback(
    e => {
      // Only prevent modal if we're actually drag selecting (not just mouse down)
      if (shouldBlockForDragSelection(e)) {
        return
      }

      e.stopPropagation()
      e.preventDefault()

      if (externalModalControl && onOpenReclassifyModal) {
        // Use external handler (for custom views to prevent unmount issues)
        onOpenReclassifyModal()
      } else {
        // Use local state (for automatic views)
        setReclassifyModalOpen(true)
      }
    },
    [externalModalControl, onOpenReclassifyModal]
  )
  const handleReclassifyModalClose = useCallback(() => {
    setReclassifyModalOpen(false)
  }, [])

  const handleRemoveFile = useCallback(() => {
    if (isUnit) {
      dispatch(
        removeUnitFileRoutine({
          bpId: effectiveBp.id,
          unitId: unit.id,
          fileId: file.id
        })
      )
    } else {
      dispatch(removeFileRoutine({ id: file.id, bpId: effectiveBp.id }))
    }
    handleModalClose()
  }, [file, effectiveBp])

  const handleTouchStart = useCallback(
    e => {
      const isMobile = window.innerWidth <= 768
      if (!isMobile || !canBeAccessed) return

      const touch = e.touches[0]
      if (!touch) return

      touchStartTimeRef.current = Date.now()
      touchStartPositionRef.current = { x: touch.clientX, y: touch.clientY }
      hasMovedRef.current = false

      longPressTimerRef.current = setTimeout(() => {
        if (!hasMovedRef.current && touchStartPositionRef.current) {
          if (onActivateMobileSelectionMode) {
            onActivateMobileSelectionMode()
          }
          if (!isSelected && onSelect) {
            onSelect(file.id, true)
          }
        }
      }, 500)
    },
    [canBeAccessed, isSelected, onSelect, onActivateMobileSelectionMode, file.id]
  )

  const handleTouchMove = useCallback(
    e => {
      if (!touchStartPositionRef.current) return

      const touch = e.touches[0]
      if (!touch) return

      const deltaX = Math.abs(touch.clientX - touchStartPositionRef.current.x)
      const deltaY = Math.abs(touch.clientY - touchStartPositionRef.current.y)

      if (deltaX > 8 || deltaY > 8) {
        hasMovedRef.current = true
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current)
          longPressTimerRef.current = null
        }
      }
    },
    []
  )

  const handleTouchEnd = useCallback(
    e => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }

      setTimeout(() => {
        touchStartPositionRef.current = null
        hasMovedRef.current = false
      }, 100)
    },
    []
  )

  const handleTouchCancel = useCallback(
    e => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
      touchStartPositionRef.current = null
      hasMovedRef.current = false
    },
    []
  )

  useEffect(() => {
    const isMobile = window.innerWidth <= 768
    if (!isMobile || !canBeAccessed || !fileElementRef.current) return

    const element = fileElementRef.current

    const touchStartHandler = (e) => {
      const touch = e.touches[0]
      if (!touch) return

      touchStartTimeRef.current = Date.now()
      touchStartPositionRef.current = { x: touch.clientX, y: touch.clientY }
      hasMovedRef.current = false

      longPressTimerRef.current = setTimeout(() => {
        if (!hasMovedRef.current && touchStartPositionRef.current) {
          if (onActivateMobileSelectionMode) {
            onActivateMobileSelectionMode()
          }
          if (!isSelected && onSelect) {
            onSelect(file.id, true)
          }
        }
      }, 500)
    }

    const touchMoveHandler = (e) => {
      if (!touchStartPositionRef.current) return

      const touch = e.touches[0]
      if (!touch) return

      const deltaX = Math.abs(touch.clientX - touchStartPositionRef.current.x)
      const deltaY = Math.abs(touch.clientY - touchStartPositionRef.current.y)

      if (deltaX > 8 || deltaY > 8) {
        hasMovedRef.current = true
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current)
          longPressTimerRef.current = null
        }
      }
    }

    const touchEndHandler = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
      setTimeout(() => {
        touchStartPositionRef.current = null
        hasMovedRef.current = false
      }, 100)
    }

    const touchCancelHandler = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
      touchStartPositionRef.current = null
      hasMovedRef.current = false
    }

    element.addEventListener('touchstart', touchStartHandler, { passive: true })
    element.addEventListener('touchmove', touchMoveHandler, { passive: true })
    element.addEventListener('touchend', touchEndHandler, { passive: true })
    element.addEventListener('touchcancel', touchCancelHandler, { passive: true })

    return () => {
      element.removeEventListener('touchstart', touchStartHandler)
      element.removeEventListener('touchmove', touchMoveHandler)
      element.removeEventListener('touchend', touchEndHandler)
      element.removeEventListener('touchcancel', touchCancelHandler)
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
      touchStartPositionRef.current = null
      hasMovedRef.current = false
    }
  }, [canBeAccessed, isSelected, onSelect, onActivateMobileSelectionMode, file.id])

  const handleMobileClick = useCallback(
    e => {
      const isMobile = window.innerWidth <= 768
      if (isMobile && isMobileSelectionMode && canBeAccessed) {
        if (onSelect) {
          onSelect(file.id, !isSelected)
        }
      }
    },
    [isMobileSelectionMode, canBeAccessed, isSelected, onSelect, file.id]
  )

  const handlePreviewFile = async e => {
    if (shouldBlockForDragSelection(e)) {
      return
    }

    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    setAnchorEl(null)

    if (handleFilePreview) {
      handleFilePreview(file)
    }

    if (isInSearchBar) {
      dispatch(markFileAsSearchedRoutine({ fileId: file.id }))
    }

    if (file.isExternal && file.shareToken) {
      dispatch(
        logExternalPreviewRoutine.trigger({
          fileId: file.id,
          shareToken: file.shareToken,
          email: file.email || file.sharedWithEmail || undefined
        })
      )
    }

    // Get URLs - either from cached state, file object, or fetch on demand
    let url = fileUrls?.url || file.url
    let originalUrl = fileUrls?.originalUrl || file.originalUrl

    // If URL not available, fetch it. For global-search results, the selected BP in
    // context is null — fall back to the building passport the file belongs to.
    const bpIdForFetch = bp?.id || file.buildingPassport?.id
    if (!url && bpIdForFetch) {
      try {
        setIsLoadingUrls(true)
        const response = await getFileUrls(bpIdForFetch, file.id)
        const urls = response.data
        url = urls.url
        originalUrl = urls.originalUrl
        setFileUrls(urls)
      } catch (error) {
        console.error('Failed to fetch file URLs:', error)
        return
      } finally {
        setIsLoadingUrls(false)
      }
    } else if (!url && !bp?.id && !file.buildingPassport?.id && estateId) {
      try {
        setIsLoadingUrls(true)
        const response = await getEstateFileUrls({ estateId, fileId: file.id })
        const urls = response.data
        url = urls.url
        originalUrl = urls.originalUrl
        setFileUrls(urls)
      } catch (error) {
        console.error('Failed to fetch file URLs:', error)
        return
      } finally {
        setIsLoadingUrls(false)
      }
    }

    if (!url) return

    // Check if file has been converted to PDF (originalUrl exists means file.url is the PDF version)
    const fileHasPdfConversion = isNotNilOrEmpty(originalUrl)
    const isPdfUrl = /\.pdf/.test(url.toLowerCase())
    const isImageUrl = /\.jpg|\.jpeg|\.png|\.svg|\.webp|\.gif/.test(url.toLowerCase())

    if (fileHasPdfConversion || isPdfUrl) {
      // Files with PDF conversions (DWG, DXF, etc.) or native PDFs open in PDF viewer
      setPdfViewerOpen(true)
    } else if (isImageUrl) {
      setImageViewerOpen(true)
    } else {
      window.open(url, '_blank')
    }
  }

  const handleFileClick = e => {
    if (isAnyOverlayOpen) return
    if (isMobileSelectionMode) {
      if (e.type === 'click' && e.detail > 0 && !e.defaultPrevented) {
        handleMobileClick(e)
      }
      return
    }

    if (
      canBeAccessed &&
      !window.__isDragSelecting &&
      !pdfViewerOpen &&
      !imageViewerOpen &&
      selectedFile?.id !== file.id
    ) {
      handlePreviewFile(e)
      return
    }

    e.stopPropagation()
  }

  const handleDownloadFile = async (e, downloadOriginal = false) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    setAnchorEl(null)

    // Get URL - either from state, file object, or fetch on demand
    let url = downloadOriginal
      ? (fileUrls?.originalUrl || file.originalUrl)
      : (fileUrls?.url || file.url)

    // If URL not available, fetch it. For global-search results, the selected BP in
    // context is null — fall back to the building passport the file belongs to.
    const bpIdForFetch = bp?.id || file.buildingPassport?.id
    if (!url && bpIdForFetch) {
      try {
        setIsLoadingUrls(true)
        const response = await getFileUrls(bpIdForFetch, file.id)
        const urls = response.data
        setFileUrls(urls)
        url = downloadOriginal ? urls.originalUrl : urls.url
      } catch (error) {
        console.error('Failed to fetch file URLs:', error)
        return
      } finally {
        setIsLoadingUrls(false)
      }
    } else if (!url && !bp?.id && !file.buildingPassport?.id && estateId) {
      try {
        setIsLoadingUrls(true)
        const response = await getEstateFileUrls({ estateId, fileId: file.id })
        const urls = response.data
        setFileUrls(urls)
        url = downloadOriginal ? urls.originalUrl : urls.url
      } catch (error) {
        console.error('Failed to fetch file URLs:', error)
        return
      } finally {
        setIsLoadingUrls(false)
      }
    }

    if (!url) {
      console.error('No URL available for download')
      return
    }

    const anchor = document.createElement('a')
    anchor.href = url
    anchor.target = '_blank'
    anchor.download = file.displayName
    anchor.click()

    const fileVersionId = pathOr('', ['versions', 0, 'id'], file)
    if (file.isExternal) {
      dispatch(
        logExternalDownloadRoutine.trigger({
          fileId: file.id,
          shareToken: file.shareToken,
          email: file.email || file.sharedWithEmail || undefined
        })
      )
    } else if (fileVersionId) {
      markFileAsViewed({
        fileId: file.id,
        fileVersionId,
        type: 'download'
      })
    }
  }

  const handleNewVersionModalOpen = () => {
    if (window.__isDragSelecting) return
    setAnchorEl(null)
    setNewVersionModalOpen(true)
  }

  const handleNewVersionModalClose = () => {
    setAnchorEl(null)
    setNewVersionModalOpen(false)
  }

  // Check if file has an original URL (either from fetched URLs or file object)
  // Note: We may not know until we fetch URLs, so we also check fileUrls state
  const originalUrl = fileUrls?.originalUrl || propOr('', 'originalUrl', file)
  const hasOriginalUrl = isNotNilOrEmpty(originalUrl)

  // Create a file object with fetched URLs merged in for viewers
  const fileWithUrls = useMemo(() => {
    if (!fileUrls) return file
    return {
      ...file,
      url: fileUrls.url || file.url,
      originalUrl: fileUrls.originalUrl || file.originalUrl,
      thumbnailUrl: fileUrls.thumbnailUrl || file.thumbnailUrl
    }
  }, [file, fileUrls])

  // Rendering the file menu options
  const renderFileMenu = () => {
    if (file.isExternal) {
      return (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          disableScrollLock
          disableAutoFocus
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
          style={{ marginTop: '10px' }}
        >
          <MenuItem onClick={handlePreviewFile}>Preview</MenuItem>
          <MenuItem onClick={e => handleDownloadFile(e)}>
            Download
          </MenuItem>
        </Menu>
      )
    }

    return (
      <Menu
        anchorEl={anchorEl}
        style={{
          marginTop: '10px',
          zIndex: 9999
        }}
        MenuListProps={{
          style: {
            zIndex: 9999,
            pointerEvents: 'auto !important'
          }
        }}
        PaperProps={{
          style: {
            zIndex: 9999,
            pointerEvents: 'auto !important'
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        open={open}
        onClose={handleClose}
        disableScrollLock
        disableAutoFocus
        disableAutoFocusItem
        disableRestoreFocus
        disableEnforceFocus
      >
        <MenuItem onClick={handlePreviewFile}>Preview</MenuItem>
        <MenuItem onClick={e => handleDownloadFile(e, false)}>
          {hasOriginalUrl ? 'Download PDF' : 'Download'}
        </MenuItem>
        {hasOriginalUrl && (
          <MenuItem onClick={e => handleDownloadFile(e, true)}>
            Download original file
          </MenuItem>
        )}
        {readOnlyAggregate && sourceBpId && (
          <MenuItem
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              setAnchorEl(null)
              handleOpenInBuilding()
            }}
          >
            Open in {sourceBpDisplayName}
          </MenuItem>
        )}
        {!estateId && (
          <>
            {!isShared && (isAggregate ? rowCanEdit : canEdit) && (
              <MenuItem
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleEditModalOpen(e)
                }}
              >
                Edit file details
              </MenuItem>
            )}

            {(isAggregate ? rowCanEdit : canEdit) && (
              <MenuItem
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  handlePermissionsModalOpen()
                }}
              >
                Manage permissions
              </MenuItem>
            )}
            {canEdit && (
              <MenuItem
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleViewedStatusModalOpen()
                }}
              >
                Views and downloads
              </MenuItem>
            )}
            {!isShared && (isAggregate ? rowCanEdit : canEdit) && !isPhoto && (
              <MenuItem onClick={handleReclassifyModalOpen}>
                Reclassify file
              </MenuItem>
            )}
            {(isAggregate ? rowCanEdit : true) && !isUnit && (
              <MenuItem data-tour-id='files.share-with-units' disabled={isFloorPlan} onClick={handleShareModalOpen}>
                Share with units
              </MenuItem>
            )}
            {!readOnlyAggregate && !isUnit && (
              <MenuItem
                disabled={isFloorPlan || !file.isSharedWithUnits}
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  setAnchorEl(null)
                  setUnshareModalOpen(true)
                }}
              >
                Unshare from units
              </MenuItem>
            )}
            {file.isSharepointFile && (isAggregate ? rowCanEdit : canEdit) && (
              <MenuItem onClick={handleMarkForSharePointCleanse}>
                Mark for deletion in SharePoint
              </MenuItem>
            )}
            {(isAggregate ? rowCanEdit : true) && !isShared && (
              <MenuItem onClick={handleModalOpen}>Remove file</MenuItem>
            )}
          </>
        )}
      </Menu>
    )
  }

  const renderBottomSheet = () => {
    const isMobile =
      forceMobileMenu ||
      window.innerWidth <= 768 ||
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ||
      (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) ||
      (typeof window !== 'undefined' && 'ontouchstart' in window)
    if (!isMobile) return null

    return (
      <>
        <BottomSheetOverlay onClick={handleBottomSheetClose} />
        <BottomSheet>
          <BottomSheetHandle />
          <BottomSheetHeader>
            <BottomSheetTitle>{displayName}</BottomSheetTitle>
            <BottomSheetCloseButton type='button' aria-label='Close' onClick={handleBottomSheetClose}>
              ×
            </BottomSheetCloseButton>
          </BottomSheetHeader>
          <BottomSheetContent>
            <BottomSheetItem
              onClick={runBottomSheetAction('preview', () => handlePreviewFile())}
              onTouchStart={runBottomSheetAction('preview', () => handlePreviewFile())}
            >
              <BottomSheetText>Preview</BottomSheetText>
            </BottomSheetItem>

            <BottomSheetItem
              onClick={runBottomSheetAction('download', () => handleDownloadFile(undefined, false))}
              onTouchStart={runBottomSheetAction('download', () => handleDownloadFile(undefined, false))}
            >
              <BottomSheetText>{hasOriginalUrl ? 'Download PDF' : 'Download'}</BottomSheetText>
            </BottomSheetItem>

            {hasOriginalUrl && (
              <BottomSheetItem
                onClick={runBottomSheetAction('downloadOriginal', () => handleDownloadFile(undefined, true))}
                onTouchStart={runBottomSheetAction('downloadOriginal', () => handleDownloadFile(undefined, true))}
              >
                <BottomSheetText>Download original file</BottomSheetText>
              </BottomSheetItem>
            )}

            {readOnlyAggregate && sourceBpId && (
              <BottomSheetItem
                onClick={runBottomSheetAction('openInBuilding', handleOpenInBuilding)}
                onTouchStart={runBottomSheetAction('openInBuilding', handleOpenInBuilding)}
              >
                <BottomSheetText>Open in {sourceBpDisplayName}</BottomSheetText>
              </BottomSheetItem>
            )}

            {!estateId && (
              <>
                {!isShared && (isAggregate ? rowCanEdit : canEdit) && (
                  <BottomSheetItem
                    onClick={runBottomSheetAction('edit', () => {
                      if (externalModalControl && onOpenEditModal) {
                        onOpenEditModal(file)
                      } else {
                        setEditModalOpen(true)
                      }
                    })}
                    onTouchStart={runBottomSheetAction('edit', () => {
                      if (externalModalControl && onOpenEditModal) {
                        onOpenEditModal(file)
                      } else {
                        setEditModalOpen(true)
                      }
                    })}
                  >
                    <BottomSheetText>Edit file details</BottomSheetText>
                  </BottomSheetItem>
                )}

                {(isAggregate ? rowCanEdit : canEdit) && (
                  <BottomSheetItem
                    onClick={runBottomSheetAction('permissions', () => setPermissionsModalOpen(true))}
                    onTouchStart={runBottomSheetAction('permissions', () => setPermissionsModalOpen(true))}
                  >
                    <BottomSheetText>Manage permissions</BottomSheetText>
                  </BottomSheetItem>
                )}

                {canEdit && (
                  <BottomSheetItem
                    onClick={runBottomSheetAction('viewedStatus', handleViewedStatusModalOpen)}
                    onTouchStart={runBottomSheetAction('viewedStatus', handleViewedStatusModalOpen)}
                  >
                    <BottomSheetText>Views and downloads</BottomSheetText>
                  </BottomSheetItem>
                )}

                {!isShared && (isAggregate ? rowCanEdit : canEdit) && !isPhoto && (
                  <BottomSheetItem
                    onClick={runBottomSheetAction('reclassify', () => {
                      if (externalModalControl && onOpenReclassifyModal) {
                        onOpenReclassifyModal()
                      } else {
                        setReclassifyModalOpen(true)
                      }
                    })}
                    onTouchStart={runBottomSheetAction('reclassify', () => {
                      if (externalModalControl && onOpenReclassifyModal) {
                        onOpenReclassifyModal()
                      } else {
                        setReclassifyModalOpen(true)
                      }
                    })}
                  >
                    <BottomSheetText>Reclassify file</BottomSheetText>
                  </BottomSheetItem>
                )}

                {(isAggregate ? rowCanEdit : true) && !isUnit && (
                  <BottomSheetItem
                    data-tour-id='files.share-with-units'
                    disabled={isFloorPlan}
                    onClick={runBottomSheetAction('share', () => setShareModalOpen(true))}
                    onTouchStart={runBottomSheetAction('share', () => setShareModalOpen(true))}
                  >
                    <BottomSheetText>Share with units</BottomSheetText>
                  </BottomSheetItem>
                )}

                {(isAggregate ? rowCanEdit : true) && !isShared && (
                  <BottomSheetItem
                    onClick={runBottomSheetAction('remove', () => setModalOpen(true))}
                    onTouchStart={runBottomSheetAction('remove', () => setModalOpen(true))}
                  >
                    <BottomSheetText>Remove file</BottomSheetText>
                  </BottomSheetItem>
                )}
              </>
            )}
          </BottomSheetContent>
        </BottomSheet>
      </>
    )
  }

  const renderFileTags = () => (
    <FileTagsBox
      className='file-tags-box'
      viewMode={viewMode}
      onClick={e => e.stopPropagation()}
    >
      {isNotNilOrEmpty(file.taggings) ? (
        <>
          {file.taggings.map(tag => (
            <FileItemTag
              key={`${file.id}-${tag.id}`}
              tag={tag}
              disabled={!canBeAccessed}
            />
          ))}
          {canBeAccessed && (
            <AttachTagToFile file={file}>
              <PlusButton>+</PlusButton>
            </AttachTagToFile>
          )}
        </>
      ) : (
        <TagsEmptyState>
          {canBeAccessed ? (
            <AttachTagToFile file={file}>
              <AddTagButton>+ Add tags</AddTagButton>
            </AttachTagToFile>
          ) : (
            <NoTagsLabel>No tags</NoTagsLabel>
          )}
        </TagsEmptyState>
      )}
    </FileTagsBox>
  )

  // Rendering the file versions section
  const renderFileVersions = () => (
    <FileVersionsBox viewMode={viewMode} onClick={e => e.stopPropagation()}>
      {/*<FileVersionsBoxHeader onClick={toggleCollapse}>*/}
      {/*  {collapsed ? 'Hide file versions' : 'Show file versions'}*/}
      {/*  <ChevronIcon collapsed={collapsed} />*/}
      {/*</FileVersionsBoxHeader>*/}
      {/*<Collapse in={collapsed} onClick={e => e.stopPropagation()}>*/}
      {/*  <ManageFileVersionsModal file={file} unit={unit} />*/}
      {/*</Collapse>*/}
    </FileVersionsBox>
  )

  // Main render method
  return (
    <>
      <FileContainer viewMode={viewMode}>
        {viewMode === 'grid' ? (
          // Grid view layout
          <>
            <FileCardWrapper
              className='file-card'
              data-file-id={file.id}
              canBeAccessed={canBeAccessed}
              isExpired={isExpired}
              hasThumbnail={showThumbnail}
              $descriptionExpanded={descriptionExpanded && viewMode === 'grid'}
              onClick={handleFileClick}
              categoryColor={categoryColor}
            >
              {!file.isExternal && !hideBulkSelect && (
                <FileCheckboxWrapper
                  onClick={e => e.stopPropagation()}
                  viewMode={viewMode}
                  title={file.isIdentityRestricted ? identityRestrictionText(file) : undefined}
                >
                  <Checkbox
                    isChecked={isSelected}
                    disabled={!canBeAccessed}
                    name={file.id}
                    onChange={(fileId, isChecked, event) => {
                      if (canBeAccessed) onSelect(fileId, isChecked, event)
                    }}
                  />
                </FileCheckboxWrapper>
              )}

              {!isSelected && (
                <ActionIconsContainer>
                  {file.isSharepointFile && (
                    <Tooltip title='Synced from SharePoint' placement='top'>
                      <SharePointBadge>
                        <CloudSyncOutlinedIcon style={{ fontSize: 16 }} />
                      </SharePointBadge>
                    </Tooltip>
                  )}
                  {isSharedWithUnits && <SharedIcon />}
                  {canBeAccessed && (
                    <IconWrapper
                      type='button'
                      aria-label={`More actions for ${displayName || 'this document'}`}
                      data-tour-id={rowIndex === 0 ? 'files.file-menu' : undefined}
                      onClick={openMenu}
                      onMouseDown={e => e.stopPropagation()}
                      onTouchStart={e => e.stopPropagation()}
                    >
                      <MoreIcon />
                    </IconWrapper>
                  )}
                </ActionIconsContainer>
              )}

              <FileCardContent>
                {showThumbnail && (
                  <ThumbnailContainer $expandable={!fileDescription}>
                    {thumbnailUrl ? (
                      <SimpleThumbnail
                        src={thumbnailUrl}
                        alt={displayName}
                        draggable={false}
                        data-testid='file-thumbnail'
                        data-file-id={file.id}
                      />
                    ) : thumbnailUrl === undefined ? (
                      <ThumbnailSkeleton />
                    ) : isExcelFile ? (
                      <ExcelIconContainer>
                        <ExcelIcon viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg">
                          <path fill="#185C37" d="M58.5 6H18c-3.3 0-6 2.7-6 6v72c0 3.3 2.7 6 6 6h60c3.3 0 6-2.7 6-6V31.5L58.5 6z"/>
                          <path fill="#21A366" d="M58.5 6V24c0 4.1 3.4 7.5 7.5 7.5H84L58.5 6z"/>
                          <path fill="#107C41" d="M84 31.5H66c-4.1 0-7.5-3.4-7.5-7.5V6L84 31.5z"/>
                          <path fill="#33C481" d="M66 31.5c-4.1 0-7.5-3.4-7.5-7.5V6L84 31.5H66z" opacity=".5"/>
                          <rect x="18" y="42" width="48" height="36" rx="3" fill="#fff"/>
                          <path fill="#185C37" d="M24 51h12v6H24zM24 60h12v6H24zM24 69h12v6H24zM39 51h12v6H39zM39 60h12v6H39zM39 69h12v6H39zM54 51h6v6h-6zM54 60h6v6h-6zM54 69h6v6h-6z"/>
                        </ExcelIcon>
                      </ExcelIconContainer>
                    ) : (
                      <DefaultFileIcon categoryColor={categoryColor}>
                        {file.isIdentityRestricted || file.isPrivate ? <LockIcon /> : <ArticleIcon />}
                      </DefaultFileIcon>
                    )}
                  </ThumbnailContainer>
                )}
                <CardFileNameWrapper>
                  <Tooltip title={displayName} placement='top'>
                    <CardFileName>
                      {highlightText(displayName, searchQuery)}
                    </CardFileName>
                  </Tooltip>
                  {file.searchScore && (
                    <Tooltip
                      title='AI Semantic Search Relevance Score'
                      placement='top'
                    >
                      <SearchScoreChip
                        label={`${Math.round(file.searchScore * 100)}%`}
                        size='small'
                      />
                    </Tooltip>
                  )}
                  {file.identityDocument && !file.isIdentityRestricted && (
                    <>
                      <IdentityBadge>Identity document</IdentityBadge>
                      <IdentityDocumentExplainer />
                    </>
                  )}
                </CardFileNameWrapper>
                {sourceBpName && (
                  <CardSourceBp title={`In ${sourceBpName}`}>
                    <ApartmentOutlinedIcon style={{ fontSize: 13 }} aria-hidden='true' />
                    <span>{sourceBpName}</span>
                  </CardSourceBp>
                )}
                {file.isIdentityRestricted || file.isIdentityUnreadable ? (
                  <IdentityRestrictedNote pending={file.isIdentityPending} unreadable={file.isIdentityUnreadable} />
                ) : !canBeAccessed && (
                  <LockedHint title={lockedExplainer}>
                    <LockIcon style={{ fontSize: 12 }} aria-hidden='true' />
                    Not shared with you
                  </LockedHint>
                )}
                <FileTypeLabel categoryColor={categoryColor}>
                  {type}
                  {!isShared && (isAggregate ? rowCanEdit : canEdit) && !isPhoto && (
                    <Tooltip title='Reclassify with AI'>
                      <AIIconButton
                        onClick={e => {
                          e.stopPropagation()
                          e.preventDefault()
                          handleReclassifyModalOpen(e)
                        }}
                      >
                        <AutoAwesomeIcon />
                      </AIIconButton>
                    </Tooltip>
                  )}
                  {isDrawing && drawingTags.map(tag => (
                    <Tooltip key={`${tag.layer}-${tag.name}`} title={`${titleCase(tag.layer.replace('_', ' '))}: ${titleCase(tag.name)}`}>
                      <DrawingInlineTag>{titleCase(tag.name)}</DrawingInlineTag>
                    </Tooltip>
                  ))}
                </FileTypeLabel>
                {hasExpirationDate && (
                  <ExpiryDate isExpired={isExpired}>
                    {isExpired ? 'Expired' : 'Expires'}:{' '}
                    {formatDate(expirationDate)}
                  </ExpiryDate>
                )}
                <VersionDateContainer>
                  <VersionDateInfo>
                    {!file.isExternal && (
                      <>
                        {file.fromEmail ? (
                          <>From email by: {file.uploadedByEmail} · </>
                        ) : uploader ? (
                          <>
                            By: {propOr('', 'firstName', uploader)}{' '}
                            {propOr('', 'lastName', uploader)}
                            {file.sharedBy && <> · Shared by: {file.sharedBy}</>}
                            {' · '}
                          </>
                        ) : null}
                      </>
                    )}
                    {file.isExternal && uploader && (
                      <>
                        Uploaded by: {propOr('', 'firstName', uploader)}{' '}
                        {propOr('', 'lastName', uploader)}
                        {' · '}
                      </>
                    )}
                    {formatDate(latestVersionCreationDate || file.createdAt)}
                  </VersionDateInfo>
                  <ButtonsContainer onClick={e => e.stopPropagation()}>
                    <AttachTagToFile file={file}>
                      <Tooltip
                        title={
                          isNotNilOrEmpty(file.taggings)
                            ? `Tags: ${file.taggings.map(tag => tag.name || tag.tag?.name || 'Unknown').join(', ')}`
                            : 'No tags'
                        }
                        placement='top'
                      >
                        <TagsButton>
                          <LocalOfferOutlinedIcon></LocalOfferOutlinedIcon>
                        </TagsButton>
                      </Tooltip>
                    </AttachTagToFile>
                    {customPath && isInSearchBar && (
                      <Tooltip title={`Path: ${customPath}`} placement='top'>
                        <PathButton>
                          <FolderOpenIcon />
                        </PathButton>
                      </Tooltip>
                    )}
                    {canBeAccessed && !isPhoto && file.canGenerateGuruInsight && (
                      <Tooltip title='Guru Insight' placement='top'>
                        <GuruInsightButton
                          data-tour-id='files.guru-insight'
                          onClick={e => {
                            e.stopPropagation()
                            e.preventDefault()
                            handleGuruInsightModalOpen(e)
                          }}
                        >
                          <AutoAwesomeIcon />
                        </GuruInsightButton>
                      </Tooltip>
                    )}
                  </ButtonsContainer>
                </VersionDateContainer>
                {fileDescription && viewMode === 'grid' && (
                  <DescriptionContainer>
                    <FileDescription
                      $expanded={descriptionExpanded}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (fileDescription.length > 100) {
                          setDescriptionExpanded(!descriptionExpanded)
                        }
                      }}
                      $isClickable={fileDescription.length > 100}
                    >
                      {fileDescription}
                    </FileDescription>
                    {fileDescription.length > 100 && (
                      <ExpandDescriptionButton
                        onClick={(e) => {
                          e.stopPropagation()
                          setDescriptionExpanded(!descriptionExpanded)
                        }}
                      >
                        {descriptionExpanded ? 'Show less' : 'Show more'}
                      </ExpandDescriptionButton>
                    )}
                  </DescriptionContainer>
                )}
                {fileDescription && viewMode !== 'grid' && (
                  <Tooltip title={fileDescription} placement='top'>
                    <FileDescription>{fileDescription}</FileDescription>
                  </Tooltip>
                )}
              </FileCardContent>

              {!canBeAccessed && !isInSearchBar && (
                <LockedFileAccess file={file} bpId={effectiveBp?.id || file.buildingPassport?.id} />
              )}

              {!isInSearchBar && (
                <FileCardFooter>
                  {renderFileTags()}
                  {renderFileVersions()}
                </FileCardFooter>
              )}
            </FileCardWrapper>
          </>
        ) : (
          // Compact list view layout - similar to folder tree
          <CompactFileWrapper
            ref={fileElementRef}
            key={file.id}
            id={file.id}
            className='file-card'
            data-file-id={file.id}
            canBeAccessed={canBeAccessed}
            isExpired={isExpired}
            isSelected={isSelected && !disableSelectionHighlight}
            onClick={handleFileClick}
            categoryColor={categoryColor}
            rowIndex={rowIndex}
            disableSelectionHighlight={disableSelectionHighlight}
          >
            {!file.isExternal && !hideBulkSelect && (
              <CompactCheckboxWrapper
                onClick={e => e.stopPropagation()}
                isMobileSelectionMode={isMobileSelectionMode}
                title={file.isIdentityRestricted ? identityRestrictionText(file) : undefined}
              >
                <Checkbox
                  isChecked={isSelected}
                  disabled={!canBeAccessed}
                  name={file.id}
                  onChange={(fileId, isChecked, event) => {
                    if (canBeAccessed) onSelect(fileId, isChecked, event)
                  }}
                />
              </CompactCheckboxWrapper>
            )}

            <CompactFileIcon categoryColor={categoryColor}>
              {file.isIdentityRestricted || file.isPrivate ? <LockIcon /> : <ArticleIcon />}
            </CompactFileIcon>

            <CompactFileContent>
              <CompactMainRow>
                <CompactFileName>
                  <CompactFileNameText>
                    {highlightText(displayName, searchQuery)}
                  </CompactFileNameText>
                  {!file.isIdentityRestricted && !canBeAccessed && (
                    <CompactLockedHint title={lockedExplainer}>
                      <LockIcon style={{ fontSize: 12 }} aria-hidden='true' />
                      Not shared with you
                    </CompactLockedHint>
                  )}

                  {customPath && isInSearchBar && (
                    <Tooltip title={`Path: ${customPath}`}>
                      <CompactPathToggle>
                        <FolderOpenIcon style={{ fontSize: 12 }} />
                      </CompactPathToggle>
                    </Tooltip>
                  )}
                </CompactFileName>
                {file.identityDocument && !file.isIdentityRestricted && (
                  <CompactIdentityBadgeWrapper>
                    <IdentityBadge>Identity document</IdentityBadge>
                    <IdentityDocumentExplainer />
                  </CompactIdentityBadgeWrapper>
                )}
                <CompactFileType categoryColor={categoryColor}>
                  <CompactFileTypeWrapper>
                    <CompactFileTypeName>{type}</CompactFileTypeName>
                    {canBeAccessed && !isShared && (isAggregate ? rowCanEdit : canEdit) && !isPhoto && (
                      <Tooltip title='Reclassify with AI'>
                        <CompactAIIconButton
                          onClick={e => {
                            e.stopPropagation()
                            e.preventDefault()
                            handleDirectReclassifyModalOpen(e)
                          }}
                        >
                          <AutoAwesomeIcon />
                        </CompactAIIconButton>
                      </Tooltip>
                    )}
                  </CompactFileTypeWrapper>
                </CompactFileType>
                {sourceBpName && (
                  <CompactSourceBp title={`In ${sourceBpName}`}>
                    <ApartmentOutlinedIcon style={{ fontSize: 13 }} aria-hidden='true' />
                    <span>{sourceBpName}</span>
                  </CompactSourceBp>
                )}
                <CompactFileDate>
                  {latestVersionCreationDate
                    ? formatDate(latestVersionCreationDate)
                    : '—'}
                </CompactFileDate>
                <CompactUploader>
                  {uploader
                    ? `${propOr('', 'firstName', uploader)} ${propOr('', 'lastName', uploader)}`
                    : '—'}
                </CompactUploader>
              </CompactMainRow>
              {!canBeAccessed && !isInSearchBar && (
                <LockedFileAccess file={file} bpId={effectiveBp?.id || file.buildingPassport?.id} compact />
              )}
              {(file.isIdentityRestricted || file.isIdentityUnreadable) && (
                <IdentityRestrictedNote pending={file.isIdentityPending} unreadable={file.isIdentityUnreadable} />
              )}
              {(() => {
                const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
                if (isMobile && viewMode === 'list' && fileDescription) {
                  return (
                    <CompactDescriptionContainer>
                      <CompactFileDescription
                        $expanded={descriptionExpanded}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (fileDescription.length > 30) {
                            setDescriptionExpanded(!descriptionExpanded)
                          }
                        }}
                        $isClickable={fileDescription.length > 30}
                      >
                        {descriptionExpanded
                          ? fileDescription
                          : fileDescription.length > 30
                            ? `${fileDescription.substring(0, 30)}...`
                            : fileDescription}
                      </CompactFileDescription>
                      {fileDescription.length > 30 && (
                        <CompactExpandButton
                          onClick={(e) => {
                            e.stopPropagation()
                            setDescriptionExpanded(!descriptionExpanded)
                          }}
                        >
                          {descriptionExpanded ? 'Show less' : 'Show more'}
                        </CompactExpandButton>
                      )}
                    </CompactDescriptionContainer>
                  )
                }
                if (fileDescription) {
                  return (
                    <CompactDescriptionRow>
                      <Tooltip title={file.aiSummary || file.summary || file.description} placement='top'>
                        <CompactAISummary>
                          {file.aiSummary || file.summary || file.description}
                        </CompactAISummary>
                      </Tooltip>
                      {isDrawing && drawingTags.length > 0 && (
                        <DrawingTagsTextLine $compact>
                          {drawingTags.map((tag, i) => (
                            <React.Fragment key={`${tag.layer}-${tag.name}`}>
                              {i > 0 && <DrawingTagSeparator>·</DrawingTagSeparator>}
                              <DrawingTagText $layer={tag.layer}>{titleCase(tag.name)}</DrawingTagText>
                            </React.Fragment>
                          ))}
                        </DrawingTagsTextLine>
                      )}
                      {productTags.length > 0 && (
                        <DrawingTagsTextLine $compact>
                          <ProductTagsLabel>Products covered:</ProductTagsLabel>
                          {productTags.map((name, i) => (
                            <React.Fragment key={name}>
                              {i > 0 && <DrawingTagSeparator>·</DrawingTagSeparator>}
                              <DrawingTagText $layer='product_guarantee'>{titleCase(name)}</DrawingTagText>
                            </React.Fragment>
                          ))}
                        </DrawingTagsTextLine>
                      )}
                    </CompactDescriptionRow>
                  )
                }
                if ((isDrawing && drawingTags.length > 0) || productTags.length > 0) {
                  return (
                    <CompactDescriptionRow>
                      {isDrawing && drawingTags.length > 0 && (
                        <DrawingTagsTextLine $compact>
                          {drawingTags.map((tag, i) => (
                            <React.Fragment key={`${tag.layer}-${tag.name}`}>
                              {i > 0 && <DrawingTagSeparator>·</DrawingTagSeparator>}
                              <DrawingTagText $layer={tag.layer}>{titleCase(tag.name)}</DrawingTagText>
                            </React.Fragment>
                          ))}
                        </DrawingTagsTextLine>
                      )}
                      {productTags.length > 0 && (
                        <DrawingTagsTextLine $compact>
                          <ProductTagsLabel>Products covered:</ProductTagsLabel>
                          {productTags.map((name, i) => (
                            <React.Fragment key={name}>
                              {i > 0 && <DrawingTagSeparator>·</DrawingTagSeparator>}
                              <DrawingTagText $layer='product_guarantee'>{titleCase(name)}</DrawingTagText>
                            </React.Fragment>
                          ))}
                        </DrawingTagsTextLine>
                      )}
                    </CompactDescriptionRow>
                  )
                }
                return null
              })()}
            </CompactFileContent>

            <CompactActions>
              {isSharedWithUnits && <CompactSharedIcon />}
              {canBeAccessed && !isPhoto && file.canGenerateGuruInsight && (
                <Tooltip title='Guru Insight' placement='top'>
                  <CompactGuruInsightButton
                    data-tour-id='files.guru-insight'
                    onClick={e => {
                      e.stopPropagation()
                      e.preventDefault()
                      handleGuruInsightModalOpen(e)
                    }}
                  >
                    <AutoAwesomeIcon />
                  </CompactGuruInsightButton>
                </Tooltip>
              )}
              {canBeAccessed && (
                <IconWrapper
                  type='button'
                  $compact
                  aria-label={`More actions for ${displayName || 'this document'}`}
                  data-tour-id={rowIndex === 0 ? 'files.file-menu' : undefined}
                  onClick={openMenu}
                  onMouseDown={e => e.stopPropagation()}
                  onTouchStart={e => e.stopPropagation()}
                >
                  <MoreIcon />
                </IconWrapper>
              )}
            </CompactActions>
          </CompactFileWrapper>
        )}

        {anchorEl && ReactDOM.createPortal(renderFileMenu(), document.body)}
        {bottomSheetOpen && ReactDOM.createPortal(renderBottomSheet(), document.body)}
      </FileContainer>

      {newVersionModalOpen && (
        <AddNewFileVersionModal
          unit={unit}
          file={file}
          open={newVersionModalOpen}
          handleClose={handleNewVersionModalClose}
          bp={effectiveBp}
        />
      )}
      {modalOpen && (
        <Modal
          open={modalOpen}
          title='Remove file'
          onClose={handleModalClose}
          onCancel={handleModalClose}
          onSubmit={handleRemoveFile}
          withSubmit
          withCancel
        >
          Do you really want to remove this file?
        </Modal>
      )}
      {editModalOpen &&
        (file.isPrivate ? (
          <EditPrivateFileModal
            open={editModalOpen}
            unit={unit}
            onClose={handleEditModalClose}
            file={file}
            bpId={effectiveBp?.id}
          />
        ) : (
          <EditFileModal
            open={editModalOpen}
            isUnit={isUnit}
            unit={unit}
            onClose={handleEditModalClose}
            floorOptions={getFloorOptions(effectiveBp)}
            file={file}
            bp={effectiveBp}
          />
        ))}
      {shareModalOpen && (
        <ShareFileModal
          open={shareModalOpen}
          onClose={handleShareModalClose}
          file={file}
          bp={effectiveBp}
        />
      )}
      {pdfViewerOpen && (
        <PdfFullScreenViewer
          open={pdfViewerOpen}
          onClose={handlePdfViewerClose}
          file={fileWithUrls}
          initialSearchQuery={searchMode === 'content' ? searchQuery : ''}
        />
      )}
      {imageViewerOpen && (
        <ImageViewer
          open={imageViewerOpen}
          onClose={handleImageViewerClose}
          file={fileWithUrls}
        />
      )}
      {guruInsightModalOpen && (
        <GuruInsightModal
          open={guruInsightModalOpen}
          onClose={handleGuruInsightModalClose}
          file={file}
          bpId={bp?.id || file.buildingPassport?.id}
          buildingPassportName={bp?.name || file.buildingPassport?.name}
        />
      )}
      {!externalModalControl && reclassifyModalOpen && (
        <ReclassifyFileModal
          open={reclassifyModalOpen}
          onClose={handleReclassifyModalClose}
          file={file}
          onOpenEditModal={handleEditModalOpen}
          bp={effectiveBp}
        />
      )}
      {permissionsModalOpen && (
        <FilePermissionsModal
          open={permissionsModalOpen}
          onClose={() => setPermissionsModalOpen(false)}
          file={file}
          bp={effectiveBp}
        />
      )}
      {viewedStatusModalOpen && (
        <ViewedStatusModal
          file={file}
          externalOpen={viewedStatusModalOpen}
          onExternalClose={() => setViewedStatusModalOpen(false)}
        />
      )}
      {unshareModalOpen && (
        <UnshareFileModal
          bp={bp}
          file={file}
          externalOpen={unshareModalOpen}
          onExternalClose={() => setUnshareModalOpen(false)}
          hideTrigger
        />
      )}
    </>
  )
}

const BottomSheetOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2147483647; /* MAX Z-INDEX */
  animation: fadeIn 0.2s ease;
  /* Asigură-te că acoperă tot ecranul chiar și cu bare de navigare */
  height: 100vh;
  width: 100vw;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const BottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  box-sizing: border-box;
  max-width: 100vw;
  overflow-x: hidden;
  background: white;
  border-radius: 20px 20px 0 0;
  z-index: 2147483647; /* MAX Z-INDEX */
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  /* Padding pentru safe area pe iPhone-uri noi */
  padding-bottom: env(safe-area-inset-bottom);

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`

const BottomSheetHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #d0d0d0;
  border-radius: 2px;
  margin: 12px auto 8px;
`

const BottomSheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
`

const BottomSheetTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 12px;
`

const BottomSheetCloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 28px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }
`

const BottomSheetContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 8px 0;
`

const BottomSheetItem = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 16px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;

  &:hover {
    background-color: #f5f5f5;
  }

  &:active {
    background-color: #e8e8e8;
  }

  ${({ disabled }) =>
  disabled &&
  css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    `}
`

const BottomSheetText = styled.span`
  font-size: 16px;
  color: #1f2937;
  flex: 1;
`

export default File
// Temporarily disabled memo to debug re-render issues
// export default memo(File, (prevProps, nextProps) => {
//   // Custom comparison to prevent re-renders during drag
//   return (
//     prevProps.file.id === nextProps.file.id &&
//     prevProps.isSelected === nextProps.isSelected &&
//     prevProps.viewMode === nextProps.viewMode &&
//     prevProps.categoryColor === nextProps.categoryColor &&
//     prevProps.type === nextProps.type &&
//     prevProps.isUnit === nextProps.isUnit &&
//     prevProps.isShared === nextProps.isShared &&
//     prevProps.isSharedWithUnits === nextProps.isSharedWithUnits &&
//     prevProps.isInSearchBar === nextProps.isInSearchBar &&
//     prevProps.showThumbnail === nextProps.showThumbnail &&
//     prevProps.customPath === nextProps.customPath &&
//     prevProps.externalModalControl === nextProps.externalModalControl &&
//     prevProps.disableSelectionHighlight === nextProps.disableSelectionHighlight &&
//     prevProps.isDragSelecting === nextProps.isDragSelecting &&
//     prevProps.canEdit === nextProps.canEdit &&
//     prevProps.rowIndex === nextProps.rowIndex &&
//     prevProps.hideBulkSelect === nextProps.hideBulkSelect &&
//     prevProps.onOpenReclassifyModal === nextProps.onOpenReclassifyModal &&
//     prevProps.onSelect === nextProps.onSelect &&
//     prevProps.onFileSelect === nextProps.onFileSelect
//   )
// })

const GuruInsightButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 8px;
  background-color: transparent;
  color: #9ca3af;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 10px;
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  &:hover {
    background-color: #f3f4f6;
    color: #6b7280;
    transform: scale(1.05);
  }

  &:focus {
    outline: none;
  }

  svg {
    font-size: 18px;
  }
`

const ListViewGuruButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 20px;
  background-color: white;
  color: ${({ theme }) => theme.colors.error};
  border: 1px solid ${({ theme }) => theme.colors.error};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  margin-right: 8px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.error};
    color: white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    transform: scale(1.05);
  }

  &:focus {
    outline: none;
  }

  svg {
    font-size: 16px;
    margin-right: 4px;
  }

  span {
    white-space: nowrap;
  }
`

const AIIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 2px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 4px;
  padding: 0;
  min-width: 18px;

  &:hover {
    background: rgba(0, 122, 255, 0.1);
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
  }

  svg {
    font-size: 13px;
    color: inherit;
    margin: 0;
    opacity: 0.7;
  }

  &:hover svg {
    opacity: 1;
  }
`

// Container to wrap both view modes
const SharePointBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0f6f78;
`

const FileContainer = styled.div`
  margin-bottom: ${props => (props.viewMode === 'grid' ? '0' : '0')};
  width: 100%;
`

// Grid view styled components
const FileCardWrapper = styled.div`
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border: 1px solid #e1e5e9;
  border-radius: 12px;
  overflow: hidden;
  height: 450px;
  cursor: ${({ canBeAccessed }) => (canBeAccessed ? 'pointer' : 'default')};
  opacity: ${({ isExpired }) => (isExpired ? 0.7 : 1)};
  background: linear-gradient(145deg, #ffffff 0%, #fafbfc 100%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.04),
    0 1px 3px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    flex-direction: column;
    height: ${({ $descriptionExpanded }) => ($descriptionExpanded ? 'auto' : '280px')};
    min-height: 280px;
    width: 100%;
    border-radius: 8px;
    box-shadow:
      0 1px 4px rgba(0, 0, 0, 0.06),
      0 1px 2px rgba(0, 0, 0, 0.04);
    align-items: stretch;
    transition: height 0.3s ease;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.08),
        0 1px 3px rgba(0, 0, 0, 0.06);
      border-color: ${({ categoryColor }) => categoryColor || '#33becc'};
    }
  }

  ${({ canBeAccessed }) =>
    !canBeAccessed &&
    css`
      cursor: not-allowed !important;

      & > *:not([data-locked-access]):not(:has([data-locked-access])),
      & > *:has([data-locked-access]) > *:not([data-locked-access]) {
        opacity: 0.5;
        filter: grayscale(100%);
      }

      * {
        pointer-events: none !important;
      }

      [data-locked-access],
      [data-locked-access] * {
        pointer-events: auto !important;
        cursor: pointer;
      }

      &:hover {
        transform: none;
        box-shadow:
          0 2px 8px rgba(0, 0, 0, 0.04),
          0 1px 3px rgba(0, 0, 0, 0.06);
      }
    `}
`

const FileCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[500]};
`

const FileCardContent = styled.div`
  padding: 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 8px 10px;
    gap: 4px;
    flex: 1;
    overflow: visible;
    justify-content: flex-start;
    min-height: 0;
    height: auto;
  }
`

const CardFileNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`

const CardSourceBp = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  margin: -4px 0 8px;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;

  svg { flex-shrink: 0; color: #2aa6b3; }

  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const LockedHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  margin: -2px 0 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
`

const IdentityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${RAG.critical.bg};
  color: ${RAG.critical.fg};
  white-space: nowrap;
`

const CompactIdentityBadgeWrapper = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin: 0 8px;

  @media (max-width: 768px) {
    margin: 0 4px;
  }
`

const CompactLockedHint = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 8px;
  padding: 0 8px;
  height: 20px;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`

const CompactSourceBp = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  max-width: 190px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 500;
  color: #4b5563;
  line-height: 24px;
  box-sizing: border-box;

  svg { flex-shrink: 0; color: #2aa6b3; }

  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    flex: 1 1 100%;
    max-width: none;
    padding: 0 8px 0 4px;
    line-height: 20px;
  }
`

const CardFileName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  word-break: break-word;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (max-width: 768px) {
    font-size: 13px;
    -webkit-line-clamp: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const FileTypeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
  padding: 4px 8px;
  background-color: ${props => {
    if (!props.categoryColor) return '#fef7ed'
    // Use full color as background for better visibility
    return props.categoryColor
  }};
  border-radius: 6px;
  color: ${props => {
    if (!props.categoryColor) return '#c2410c'

    // Determine text color based on background brightness
    const color = props.categoryColor

    // Light backgrounds - use dark text
    if (color === '#bbeaf0' || color === '#ffc111' || color === '#ffaf7f') {
      return '#0c132b' // Dark blue/black for contrast
    }

    // Dark/medium backgrounds - use white text
    return '#ffffff'
  }};
  border: 1px solid
  ${props => (props.categoryColor ? props.categoryColor : '#fed7aa')};
  min-height: 28px;
  height: auto;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    font-size: 10px;
    padding: 2px 6px;
    margin-bottom: 3px;
    min-height: 20px;
    max-height: 24px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
  }
`


const VersionDateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0px;
  gap: 8px;
`

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 768px) {
    gap: 2px;
    flex-shrink: 0;
  }
`

const TagsButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  color: #9ca3af;

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
  }

  &:hover {
    background-color: #f3f4f6;
    color: #6b7280;
  }

  svg {
    font-size: 18px;

    @media (max-width: 768px) {
      font-size: 14px;
    }
  }

  span {
    font-size: 12px;
    line-height: 1;
  }
`

const PathButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  color: #9ca3af;

  &:hover {
    background-color: rgba(51, 190, 204, 0.1);
    color: #33becc;
  }

  svg {
    font-size: 18px;
  }
`

const VersionDateInfo = styled.div`
  font-size: 11px;
  color: #000000;
  font-weight: 600;
  line-height: 1.3;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 10px;
    line-height: 1.2;
  }
`

const FileCardFooter = styled.div`
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  border-top: 1px solid ${({ theme }) => theme.colors.grey[500]};

  @media (max-width: 768px) {
    border-top: none;
    border-left: 1px solid ${({ theme }) => theme.colors.grey[500]};
    width: auto;
    min-width: fit-content;
  }
`

const ExpiryDate = styled.div`
  display: inline-block;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${({ theme, isExpired }) =>
    isExpired ? theme.colors.error + '18' : theme.colors.secondary + '15'};
  color: ${({ theme, isExpired }) =>
    isExpired ? theme.colors.error : theme.colors.secondary};
  font-weight: 500;
`

const SharedIcon = styled(ShareIcon)`
  color: ${({ theme }) => theme.colors.secondary};
  top: 1px;
  right: 1px;
  font-size: 10px;
`

const FileVersionsBox = styled.div`
  box-sizing: border-box;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  margin-bottom: ${props => (props.viewMode === 'grid' ? '0' : '1px')};
  border: ${props =>
    props.viewMode === 'grid'
      ? 'none'
      : `1px solid ${props.theme.colors.grey[500]}`};
  border-radius: ${props =>
    props.viewMode === 'grid' ? '0 0 8px 8px' : '0 0 4px 4px'};
`

const FileVersionsBoxHeader = styled.div`
  width: 100%;
  text-align: center;
  font-size: 12px;
  cursor: pointer;
  padding: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ChevronIcon = styled(KeyboardArrowDownIcon)`
  font-size: 14px !important;
  margin-left: 3px;
  color: ${({ theme }) => theme.colors.haiti};
  transform: rotate(${({ collapsed }) => (collapsed ? '180deg' : '0')});
`

const TopTag = styled.div`
  position: absolute;
  top: -10px;
  left: 5px;
  padding: 2px 5px;
  font-size: 11px;
  background-color: #fff;
  color: ${({ theme }) => theme.colors.darkGrey};
`

const DetailsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-right: 10px;
  box-sizing: border-box;
`

const Details = styled.div`
  font-size: 11px;
  text-align: right;
  color: ${({ theme }) => theme.colors.black};
`

const FileType = styled.div`
  display: flex;
  align-items: center;
  font-weight: bold;
  gap: 4px;
  max-width: 380px;
`

const FileTypeName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`

const FileTypeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-end;
  text-align: right;
  min-width: 0;
  max-width: 450px;
  flex-shrink: 1;
`

const FileNameSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const FileMetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-top: 1px;
  margin-left: 15px;

  span {
    white-space: nowrap;
  }
`

const FileTypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const MoreIcon = styled(MoreVertIcon)`
  color: #4d4d56;
  font-size: 24px !important;
  transition: color 0.2s;
`

const IconWrapper = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ $compact }) => ($compact ? '36px' : '40px')};
  height: ${({ $compact }) => ($compact ? '36px' : '40px')};
  padding: 0;
  border: none;
  border-radius: 8px;
  background: ${({ $compact }) => ($compact ? 'transparent' : 'rgba(255, 255, 255, 0.9)')};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f1f1f3;
  }

  &:hover svg {
    color: ${({ theme }) => theme.colors.secondary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 1px;
  }
`

const FileIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;

  svg {
    width: 24px;
    height: 24px;
  }
`

const FileDetailsWrapper = styled.div`
  box-sizing: border-box;
  justify-content: flex-end;
  align-items: flex-start;
  display: flex;
  max-width: 60%;
  gap: 12px;
`

const FileName = styled.div`
  margin-left: 12px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
`

const FileWrapper = styled.div`
  box-sizing: border-box;
  cursor: pointer;
  width: 100%;
  height: 85px;
  border: 1px solid ${({ categoryColor }) => categoryColor || '#dfe4f6'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 5px 5px 0 0;
  transition: all 0.3s;
  opacity: ${({ isExpired }) => (isExpired ? 0.6 : 1)};
  background-color: ${({ isExpired, theme, categoryColor }) =>
    isExpired
      ? theme.colors.grey[400]
      : categoryColor
        ? `${categoryColor}10`
        : 'transparent'};
  position: relative;
  padding-right: 20px;

  &:hover {
    border-color: ${({ categoryColor }) => categoryColor || '#dfe4f6'};
  }

  ${({ canBeAccessed }) =>
    !canBeAccessed &&
    css`
      color: ${({ theme }) => theme.colors.grey[700]} !important;
      cursor: default !important;

      svg {
        cursor: default !important;
      }

      & div {
        color: ${({ theme }) => theme.colors.grey[700]} !important;
      }

      &:hover {
        border-color: ${({ theme }) => theme.colors.grey[700]};
      }
    `}

  /* Add a colored accent on the left side */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background-color: ${({ categoryColor }) => categoryColor || 'transparent'};
  }
`

const NameWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding-left: 35px;
  max-width: 300px;
  overflow: hidden;

  @media (min-width: 1300px) {
    max-width: 500px;
  }
`

const FileTagsBox = styled.div`
  padding: ${props => (props.viewMode === 'grid' ? '0px' : '2px 0px')};
  display: ${props => (props.viewMode === 'grid' ? 'none' : 'flex')};
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  border-bottom: none;
  border-left: none;
  border-right: none;
  background-color: transparent;
  min-height: 0px;
`

const TagsLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
`
const TagsEmptyState = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 4px 0;
`

const NoTagsLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
  font-style: italic;
`

const AddTagButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.grey[400]};
  border-radius: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[200]};
    color: ${({ theme }) => theme.colors.secondary};
    border-color: ${({ theme }) => theme.colors.grey[500]};
  }
`

const PlusButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[200]};
    color: ${({ theme }) => theme.colors.haiti};
  }
`
const ThumbnailContainer = styled.div`
  width: 100%;
  min-height: 200px;
  ${({ $expandable }) => $expandable ? 'flex: 1;' : 'height: 200px; flex-shrink: 0;'}
  background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
    height: 80px;
    min-height: 80px;
    flex: 0 0 80px;
    min-width: 100%;
    max-width: 100%;
    background: #f5f7fa;
  }
`

// Friendly skeleton with subtle loading bar for thumbnails
const ThumbnailSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: #f5f6f7;
  position: relative;

  /* Subtle loading bar at bottom */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    width: 30%;
    background: linear-gradient(90deg, #33becc, #5fd4e0);
    border-radius: 0 2px 2px 0;
    animation: loadingBar 1.2s ease-in-out infinite;
  }

  @keyframes loadingBar {
    0% {
      width: 0%;
      left: 0;
    }
    50% {
      width: 60%;
      left: 20%;
    }
    100% {
      width: 0%;
      left: 100%;
    }
  }
`

const SkeletonShimmer = styled.div`
  display: none;
`

const SimpleThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-select: none;

  @media (max-width: 768px) {
    object-fit: cover;
    background: #f5f7fa;
    width: 100%;
    height: 100%;
  }
`

const FileThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${props => props.objectFit || 'cover'};
  transition: transform 0.3s ease;
`

const ThumbnailImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: ${props => props.objectFit || 'cover'};
  transition: opacity 0.3s ease;
  background-color: white;
  will-change: opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
`

const PlaceholderThumbnail = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;
  position: relative;
  will-change: transform;
  backface-visibility: hidden;
  transform: translateZ(0);
`

const DefaultFileIcon = styled.div`
  width: 80px;
  height: 100px;
  background: ${props => props.categoryColor || '#ffffff'};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 24px;
    height: 24px;
    background: linear-gradient(
      135deg,
      transparent 50%,
      rgba(0, 0, 0, 0.15) 50%
    );
    border-top-right-radius: 12px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 24px;
    height: 24px;
    background: ${props => {
      const color = props.categoryColor
      if (color === '#bbeaf0') return '#a5d8e0'
      if (color === '#ffc111') return '#e6ac0f'
      if (color === '#ffaf7f') return '#e69a72'
      if (color === '#0c132b') return '#1a2242'
      if (color === '#36becd') return '#30aab8'
      if (color === '#cd2000') return '#b81d00'
      return '#e0e0e0'
    }};
    border-top-right-radius: 4px;
    border-bottom-left-radius: 8px;
    transform: translate(-2px, 2px);
  }

  svg {
    width: 40px;
    height: 40px;
    color: ${props => {
      const color = props.categoryColor
      // Light backgrounds - use dark icon
      if (color === '#bbeaf0' || color === '#ffc111' || color === '#ffaf7f') {
        return '#0c132b'
      }
      // Dark/medium backgrounds - use white icon
      return '#ffffff'
    }};
    z-index: 1;
  }
`

const ExcelIconContainer = styled.div`
  width: 80px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
`

const ExcelIcon = styled.svg`
  width: 70px;
  height: 70px;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15));
`

const ActionIconsContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2;

  ${SharedIcon} {
    margin-right: 4px;
  }

  @media (max-width: 768px) {
    top: 6px;
    right: 6px;
    gap: 6px;
  }
`
const FileCheckboxWrapper = styled.div`
  position: absolute;
  z-index: 10;
  background: ${({ viewMode }) =>
    viewMode === 'grid' ? 'white' : 'transparent'};
  padding: 2px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    outline: none !important;
    border: none !important;
    box-shadow: none !important;

    &:focus {
      outline: none !important;
      box-shadow: none !important;
    }

    &:checked {
      outline: none !important;
      box-shadow: none !important;
    }

    @media (max-width: 768px) {
      width: 18px;
      height: 18px;
    }
  }

  ${({ viewMode }) =>
    viewMode === 'grid'
      ? css`
        top: 12px;
        left: 12px;

        @media (max-width: 768px) {
          top: 8px;
          left: 8px;
          padding: 4px;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 6px;
        }
      `
      : css`
        top: 23px;
        left: 5px;
        transform: translateY(-50%);
      `};
`
const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 0px;
  margin-bottom: 4px;
  position: relative;
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  @media (max-width: 768px) {
    flex: 0 0 auto;
    overflow: visible;
    min-height: auto;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
  }
`

const FileDescription = styled.div`
  font-size: 12px;
  color: #1f2937;
  line-height: 1.4;
  word-break: break-word;
  font-weight: 500;
  white-space: pre-wrap;

  ${({ $expanded, $isClickable }) =>
    !$expanded && $isClickable
      ? css`
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 6;
        -webkit-box-orient: vertical;
        text-overflow: ellipsis;

        @media (max-width: 768px) {
          -webkit-line-clamp: 2;
        }
      `
      : css`
        display: block;
        height: auto;

        @media (max-width: 768px) {
          overflow: visible;
          max-height: none;
        }
      `}

  ${({ $isClickable }) =>
    $isClickable &&
    css`
      cursor: pointer;
      transition: color 0.2s ease;
      &:hover {
        color: ${({ theme }) => theme.colors.primary};
      }
    `}

  @media (max-width: 768px) {
    font-size: 11px;
    ${({ $expanded }) => !$expanded && css`-webkit-line-clamp: 2;`}
  }
`

const ExpandDescriptionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  margin-top: 2px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  text-align: left;
  font-weight: 600;
  transition: opacity 0.2s ease;
  align-self: flex-start;
  flex-shrink: 0;
  position: relative;
  z-index: 1;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }

  &:active {
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    font-size: 10px;
  }
`

const FileTypeDescription = styled.div`
  font-size: 11px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.grey[700]};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  word-break: break-word;
  max-height: 16px;
  max-width: 400px;
  margin-top: 1px;
  text-align: right;
`

// Compact list view components - similar to folder tree
const CompactFileWrapper = styled.div`
  display: flex;
  align-items: stretch;
  padding: 0;
  cursor: ${({ canBeAccessed }) => (canBeAccessed ? 'pointer' : 'default')};
  transition: all 0.15s ease;
  background-color: ${({
                         isSelected,
                         rowIndex,
                         categoryColor,
                         disableSelectionHighlight
                       }) => {
    if (disableSelectionHighlight) {
      return rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa'
    }
    // Only show selection highlight when disableSelectionHighlight is false
    if (isSelected) return `${categoryColor || '#007AFF'}22`
    return rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa'
  }};
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  margin: 0;
  opacity: ${({ isExpired }) => (isExpired ? 0.6 : 1)};
  min-height: 48px;
  user-select: none;
  position: relative;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
    overflow-x: hidden;
    width: 100%;
    max-width: 100%;
    min-height: auto;
    height: auto;
    padding: 8px 0;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background-color: ${({
                           isSelected,
                           rowIndex,
                           disableSelectionHighlight
                         }) => {
      if (disableSelectionHighlight) {
        return rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa'
      }
      if (isSelected) return '#e6f7f9'
      return rowIndex % 2 === 0 ? '#ffwffff' : '#f8f9fa'
    }};
  }

  &:hover {
    background-color: ${({
                           isSelected,
                           categoryColor,
                           disableSelectionHighlight
                         }) => {
      if (disableSelectionHighlight) return '#f0f0f0'
      return isSelected ? `${categoryColor || '#007AFF'}33` : '#e8f4ff'
    }};
    border: none !important;
    outline: none !important;
    box-shadow: none !important;

    @media (max-width: 768px) {
      background-color: ${({
                             isSelected,
                             disableSelectionHighlight
                           }) => {
        if (disableSelectionHighlight) return '#f0f0f0'
        return isSelected ? '#bbdefb' : '#f5f5f5'
      }};
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
    }
  }

  &:focus,
  &:focus-within {
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
  }

  ${({ canBeAccessed }) =>
    !canBeAccessed &&
    css`
      cursor: not-allowed !important;

      & > *:not([data-locked-access]):not(:has([data-locked-access])),
      & > *:has([data-locked-access]) > *:not([data-locked-access]) {
        opacity: 0.5;
        filter: grayscale(100%);
      }

      * {
        pointer-events: none !important;
      }

      [data-locked-access],
      [data-locked-access] * {
        pointer-events: auto !important;
        cursor: pointer;
      }
    `}
`

const CompactCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 100%;
  padding-top: 12px;

  @media (max-width: 768px) {
    display: none; /* Hide checkbox completely on mobile - use highlight only */
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    margin: 0;
    outline: none !important;
    border: none !important;
    box-shadow: none !important;

    @media (max-width: 768px) {
      width: 18px;
      height: 18px;
    }

    &:focus {
      outline: none !important;
      box-shadow: none !important;
    }

    &:checked {
      outline: none !important;
      box-shadow: none !important;
    }
  }
`

const CompactFileIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 100%;
  padding-top: 12px;

  @media (max-width: 768px) {
    align-items: flex-start;
    padding-top: 10px;
    width: 40px;
  }

  svg {
    font-size: 16px;
    color: ${props => props.categoryColor || '#86868b'};

    @media (max-width: 768px) {
      font-size: 18px;
    }
  }
`

const CompactFileContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 100%;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 0;
    min-width: 0;
    overflow: visible;
    justify-content: flex-start;
    gap: 4px;
  }
`

const CompactFileName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #1d1d1f;
  line-height: 24px;
  padding: 0 12px 0 8px;
  flex: 2;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    padding: 0 8px 0 4px;
    gap: 4px;
  }
`

const CompactFileType = styled.div`
  position: relative;
  font-size: 13px;
  font-weight: 700;
  color: ${props => {
    if (!props.categoryColor) return '#007AFF'

    // Keep colors very close to original but slightly more saturated for visibility
    const color = props.categoryColor
    if (color === '#bbeaf0') return '#a5d8e6'
    if (color === '#36becd') return '#2aa3b8'
    if (color === '#ffc111') return '#e6ac0f'
    if (color === '#cd2000') return '#b81d00'
    if (color === '#ffaf7f') return '#e69a72'
    if (color === '#0c132b') return '#1a2242'
    return color
  }};
  white-space: nowrap;
  line-height: 24px;
  padding: 0 12px;
  flex: 1.5;
  min-width: 150px;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 0 4px;
    min-width: auto;
    max-width: none;
    flex: 0 0 auto;
    width: auto;
    line-height: 18px;
    white-space: nowrap;
    margin-bottom: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const CompactFileTypeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`

const CompactFileTypeName = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    white-space: normal;
  }
`

const CompactAIIconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  margin-left: 4px;

  @media (max-width: 768px) {
    position: static;
    transform: none;
    width: 18px;
    height: 18px;
    padding: 4px;
    margin-left: 4px;
  }

  &:hover {
    background: rgba(0, 122, 255, 0.1);
    transform: translateY(-50%) scale(1.1);

    @media (max-width: 768px) {
      transform: scale(1.1);
    }
  }

  &:focus {
    outline: none;
  }

  svg {
    font-size: 11px;
    color: inherit;
    opacity: 0.7;

    @media (max-width: 768px) {
      font-size: 14px;
    }
  }

  &:hover svg {
    opacity: 1;
  }
`

const CompactSharedIcon = styled(ShareIcon)`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 10px !important;
`

const CompactMetaInfo = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: #666;
  opacity: 0.7;
`

const CompactMainLine = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
`

const CompactMainRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 24px;
  overflow-x: hidden;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    height: auto;
    gap: 8px;
    padding-bottom: 0;
    flex-wrap: wrap;
    overflow-x: hidden;
    width: 100%;
    max-width: 100%;
  }
`

const CompactFileDate = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: #86868b;
  white-space: nowrap;
  line-height: 24px;
  padding: 0 12px;
  flex: 1;
  min-width: 60px;

  max-width: 70px;

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 0 4px;
    min-width: auto;
    max-width: none;
    flex: 0 0 auto;
    width: auto;
    line-height: 18px;
    margin-bottom: 0;
    white-space: nowrap;
  }
`

const CompactUploader = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: #86868b;
  white-space: nowrap;
  line-height: 24px;
  padding: 0 12px;
  flex: 1.5;
  min-width: 120px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 0 4px;
    min-width: auto;
    max-width: none;
    flex: 0 0 auto;
    width: auto;
    line-height: 18px;
    white-space: nowrap;
    margin-bottom: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const CompactDescriptionRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 12px 2px 8px;
  gap: 6px;
  min-width: 0;
`

const CompactAISummary = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #86868b;
  line-height: 1.3;
  padding: 0;
  margin-left: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-height: 16px;
  opacity: 0.8;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 10px;
    padding: 0 4px 2px 0;
    margin-top: 2px;
    max-height: 14px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }
`

const CompactDescriptionContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 4px;
    padding: 0 4px 0 0;
    width: 100%;
    flex: 0 0 auto;
  }
`

const CompactFileDescription = styled.div`
  font-size: 11px;
  color: #86868b;
  line-height: 1.3;
  word-break: break-word;
  font-weight: 400;
  white-space: pre-wrap;

  ${({ $expanded, $isClickable }) =>
    !$expanded && $isClickable
      ? css`
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        max-height: 14px;
        text-overflow: ellipsis;
      `
      : css`
        display: block;
        height: auto;
      `}

  ${({ $isClickable }) =>
    $isClickable &&
    css`
      cursor: pointer;
      transition: color 0.2s ease;
      &:hover {
        color: #666;
      }
    `}

  @media (max-width: 768px) {
  font-size: 10px;
  line-height: 1.4;
}
`

const CompactExpandButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  margin-top: 2px;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  text-align: left;
  font-weight: 600;
  transition: opacity 0.2s ease;
  align-self: flex-start;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }

  &:active {
    opacity: 0.6;
  }
`

const CompactGuruInsightButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  color: #9ca3af;

  &:hover {
    background-color: #f3f4f6;
    color: #6b7280;
  }

  &:focus {
    outline: none;
  }

  svg {
    font-size: 16px;
  }

  @media (max-width: 768px) {
    padding: 2px;

    svg {
      font-size: 14px;
    }
  }
`

const CompactActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-shrink: 0;
  width: 60px;
  height: 100%;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  @media (max-width: 768px) {
    align-items: flex-start;
    padding-top: 10px;
    width: auto;
    min-width: 50px;
  }

  &:hover {
    opacity: 1;
  }
`

const CompactPathToggle = styled.div`
  background: transparent;
  border: none;
  border-radius: 3px;
  padding: 2px 4px;
  cursor: pointer;
  margin-left: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(51, 190, 204, 0.1);
    opacity: 1;
  }
`

const CompactFileNameText = styled.span`
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
  max-width: 100%;
`

const SearchScoreChip = styled(Chip)`
  && {
    height: 18px;
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
    width: fit-content;
    background-color: ${({ theme }) => theme.colors.secondary};
    color: white;
    border: none;

    .MuiChip-label {
      padding: 0 5px;
    }
  }
`


const DrawingInlineTag = styled.span`
  background: #ffffff;
  color: #1e293b;
  font-size: 9px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 3px;
  white-space: nowrap;
  margin: 2px 0;
`

const DARK_TAG_COLORS = {
  drawing_type: '#93c5fd',
  discipline: '#c4b5fd',
  system: '#fca5a5',
  element: '#fdba74',
  space: '#6ee7b7',
  external: '#5eead4',
  context: '#fcd34d',
  specialist: '#f0abfc',
  direction: '#7dd3fc',
  status: '#fde68a',
  product_guarantee: '#5eead4',
}

const LIGHT_TAG_COLORS = {
  drawing_type: '#33becc',
  discipline: '#5b21b6',
  system: '#9f1239',
  element: '#92400e',
  space: '#166534',
  external: '#115e59',
  context: '#b45309',
  specialist: '#7e22ce',
  direction: '#0369a1',
  status: '#854d0e',
  product_guarantee: '#0f766e',
}

const DrawingTagText = styled.span`
  ${({ $layer, $dark }) => {
    const colors = $dark ? DARK_TAG_COLORS : LIGHT_TAG_COLORS
    const color = colors[$layer] || ($dark ? '#e2e8f0' : '#475569')
    const bold = $layer === 'drawing_type' || $layer === 'discipline' || $layer === 'status'
    return css`color: ${color}; ${bold ? 'font-weight: 600;' : ''}`
  }}
`

const DrawingTagSeparator = styled.span`
  color: ${({ $dark }) => $dark ? 'rgba(255,255,255,0.35)' : '#94a3b8'};
  margin: 0 4px;
`

const DrawingTagsInline = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  flex-wrap: wrap;
`

const DrawingTagsTextLine = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
  font-size: ${({ $compact }) => ($compact ? '11px' : '12px')};
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.grey[200]};
  min-width: 0;
`

const ProductTagsLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[300]};
  margin-right: 2px;
`

const DrawingTagChip = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  line-height: 18px;

  ${({ $variant, $status }) => {
    if ($variant === 'status') {
      const s = ($status || '').toLowerCase()
      if (s === 'approved') return css`background-color: #d4edda; color: #155724;`
      if (s === 'as-built') return css`background-color: #d1ecf1; color: #0c5460;`
      if (s === 'proposed') return css`background-color: #e0e7ff; color: #3730a3;`
      return css`background-color: #fff3cd; color: #856404;`
    }
    if ($variant === 'drawing_type') return css`background-color: #e8eaf6; color: #283593;`
    if ($variant === 'discipline') return css`background-color: #e8eaf6; color: #283593;`
    if ($variant === 'system') return css`background-color: #fce4ec; color: #880e4f;`
    if ($variant === 'element') return css`background-color: #efebe9; color: #4e342e;`
    if ($variant === 'space') return css`background-color: #e8f5e9; color: #1b5e20;`
    if ($variant === 'external') return css`background-color: #e0f2f1; color: #004d40;`
    if ($variant === 'context') return css`background-color: #fff8e1; color: #f57f17;`
    if ($variant === 'specialist') return css`background-color: #f3e5f5; color: #6a1b9a;`
    if ($variant === 'direction') return css`background-color: #e1f5fe; color: #01579b;`
    return css`background-color: #f5f5f5; color: #616161;`
  }}

`

const PermissionsModalContent = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 70vh;

  @media (max-width: 768px) {
    max-height: 80vh;
  }
`

const PermissionsDescription = styled.div`
  margin-bottom: 20px;
  color: #666;
`

const PermissionsSelectAllButton = styled.button`
  align-self: flex-start;
  margin-bottom: 20px;
  border: 1px solid #33becc;
  border-radius: 4px;
  padding: 6px 12px;
  background: none;
  color: #33becc;
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
`

const PermissionsUserList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 5px;
  padding-right: 4px;
`

const PermissionsUserItem = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${({ $isSelected }) => ($isSelected ? '#33becc' : '#e0e0e0')};
  border-radius: 4px;
  margin-bottom: 8px;
  padding: 12px;
  background: ${({ $isSelected }) => ($isSelected ? '#f0fbfc' : '#fff')};
  text-align: left;
  cursor: ${({ $isLocked }) => ($isLocked ? 'default' : 'pointer')};
  opacity: ${({ $isLocked }) => ($isLocked ? 0.75 : 1)};
`

const PermissionsUserNote = styled.div`
  margin-top: 4px;
  color: #8a94a6;
  font-size: 11px;
  font-style: italic;
`

const PermissionsUserEmail = styled.div`
  color: #666;
  font-size: 12px;
`

const PermissionsUserName = styled.div`
  margin: 2px 0;
  font-size: 14px;
  font-weight: 600;
`

const PermissionsUserRoles = styled.div`
  color: #666;
  font-size: 11px;
`

const PermissionsCheck = styled.div`
  color: #33becc;
  font-size: 20px;
  font-weight: bold;
`

const PermissionsEmptyState = styled.div`
  padding: 20px;
  color: #666;
  text-align: center;
`

const PermissionsActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  border-top: 1px solid #e0e0e0;
  padding-top: 20px;
`

const PermissionsCancelButton = styled.button`
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px 16px;
  background: #fff;
  cursor: pointer;
`

const PermissionsSaveButton = styled.button`
  border: 0;
  border-radius: 4px;
  padding: 8px 16px;
  background: ${({ disabled }) => (disabled ? '#ccc' : '#33becc')};
  color: #fff;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`
